import { Icon, LoadingIcon, Modal, Typography } from '@/primer/components';
import { useCampaignGuidance } from '@/api';
import { getGuidanceCopy } from '@/utils/contentGuidance';

interface ContentRequirementsProps {
  /** Campaign whose UGC guidance to show. */
  campaignId: number;
  /** Whether the sheet is open. */
  isOpen: boolean;
  /** Called when the sheet is dismissed (X, Escape, backdrop). */
  onClose: () => void;
  /** Called when the creator confirms they understand the requirements. */
  onAcknowledge: () => void;
}

/**
 * Content requirements sheet — shows the campaign's UGC guidance (do's/don'ts +
 * example imagery) so the creator knows what to capture. Fetches only while open.
 */
export function ContentRequirements({
  campaignId,
  isOpen,
  onClose,
  onAcknowledge,
}: ContentRequirementsProps) {
  const { data, isLoading, error } = useCampaignGuidance(campaignId, isOpen);

  const results = data?.results ?? [];
  const dos = results.filter((item) => item.type === 'ENCOURAGE');
  const donts = results.filter((item) => item.type === 'AVOID');
  const inspirations = dos.filter((item) => !!item.imageUrl);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      variant="bottom-sheet"
      ariaLabel="Content requirements"
      primaryButton={{ label: 'I understand the requirements', onClick: onAcknowledge }}
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <Typography variant="header" size="h1" as="h2">
            Content requirements
          </Typography>
          <Typography variant="body" size="medium" color="bodylight">
            Please review these content requirements before creating your content.
          </Typography>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-6">
            <LoadingIcon size="large" />
          </div>
        ) : error ? (
          <Typography variant="body" size="small" color="error">
            We couldn't load the content requirements. You can still continue.
          </Typography>
        ) : (
          <>
            {(dos.length > 0 || donts.length > 0) && (
              <div className="flex flex-col gap-3 rounded-medium border border-default p-4">
                {dos.map((item, index) => (
                  <div key={`${item.textCode}-${index}`} className="flex items-center gap-3">
                    <span className="flex-shrink-0">
                      <Icon name="circle-tick-filled" size="medium" color="success" />
                    </span>
                    <Typography variant="body" size="medium" as="span">
                      {getGuidanceCopy(item.textCode)}
                    </Typography>
                  </div>
                ))}
                {donts.map((item, index) => (
                  <div key={`${item.textCode}-${index}`} className="flex items-center gap-3">
                    <span className="flex-shrink-0">
                      <Icon name="circle-close" size="medium" color="error" />
                    </span>
                    <Typography variant="body" size="medium" as="span">
                      {getGuidanceCopy(item.textCode)}
                    </Typography>
                  </div>
                ))}
              </div>
            )}

            {inspirations.length > 0 && (
              <div className="flex flex-col gap-2">
                <Typography variant="header" size="h3" as="h3">
                  Inspirations
                </Typography>
                <div className="flex gap-3 overflow-x-auto">
                  {inspirations.map((item, index) => (
                    <img
                      key={`${item.imageUrl}-${index}`}
                      src={item.imageUrl}
                      alt={getGuidanceCopy(item.textCode)}
                      className="h-48 w-40 flex-shrink-0 rounded-medium object-cover"
                    />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

export default ContentRequirements;
